import { useEffect, useState } from 'react';

import { useSetAtom } from 'jotai';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { getAuthFromNative } from '@/utils/auth';
import { isCurrentEnvironment } from '@/utils/environment';

import Constants from '@/constants';

import { OktaUserRaw } from '@/types';

import packagejson from '../../../package.json';

import { sessionOverridesAtom } from '@/atoms/sessionOverrides';
import oktaAuth, { subscribeToAuthStateChange } from '@/okta/oktaConfig';

let userSet = false;

const allowedSearchParams = ['updateResource'];

export const useUserAuth = () => {
  const [userAuthenticated, setUserAuthenticated] = useState(false);
  const [user, setUser] = useState<OktaUserRaw | undefined>();
  const setSessionOverrides = useSetAtom(sessionOverridesAtom);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    for (const [key, value] of searchParams) {
      if (allowedSearchParams.includes(key) && value === 'true') {
        setSessionOverrides(prev => prev.add(key));
      }
    }

    if (!isCurrentEnvironment(Constants.DEV)) {
      console.log(`ver. ${packagejson.version}`);
    }

    if (Constants.IS_IN_CYPRESS_TEST_LOCAL || Constants.IS_IN_JEST_TEST) {
      setUserAuthenticated(true);
      userSet = true;
      return;
    }

    const { nativeUser } = getAuthFromNative();
    if (nativeUser) {
      setUserAuthenticated(true);
      setUser(nativeUser);
    }

    // Check current auth state immediately (in case oktaAuth.start() already fired)
    const currentAuthState = oktaAuth.authStateManager.getAuthState();
    if (
      currentAuthState?.isAuthenticated &&
      currentAuthState.idToken?.claims &&
      !userSet
    ) {
      setUserAuthenticated(true);
      setUser(currentAuthState.idToken.claims as unknown as OktaUserRaw);
      userSet = true;
    }

    const { unsubscribe } = subscribeToAuthStateChange(({ authState }) => {
      sendNativeMessage({
        type: 'authStateManagerEvent',
        payload: {
          isAuthenticated: authState.isAuthenticated,
        },
      });

      if (authState.isAuthenticated && authState.idToken?.claims && !userSet) {
        setUserAuthenticated(true);
        setUser(authState.idToken.claims as unknown as OktaUserRaw);
        userSet = true;
      }
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { userAuthenticated, user };
};
